import React, { useEffect, useState } from "react";
import { TextField, Button, Select, MenuItem, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, List, ListItem } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { query, where, getDocs, getCountFromServer } from "firebase/firestore";
import { Person, useFirebaseService } from "../../../data/FirebaseService";
import { toast } from "react-toastify";
import { Group } from "./GroupsPage";

interface AddGroupDialogProps {
    onGroupAdded: (group: Group) => void;
}

export const AddGroupDialog: React.FC<AddGroupDialogProps> = ({ onGroupAdded }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState<boolean>(false);
    const firebaseService = useFirebaseService();

    const [groupName, setGroupName] = useState("");

    const checkGroupName = async (groupName: string): Promise<boolean> => {
        if (firebaseService && groupName) {
            const groupsRef = await firebaseService.getCollection(`groups`);
            const q = query(groupsRef,
                where('name', "==", groupName),
            );
            const snapshot = await getCountFromServer(q); // Get the count for the query
            return snapshot.data().count === 0; // Get the count from the snapshot
        }
        else {
            return false;
        }
    }

    const handleCreateGroup = async () => {
        setLoading(true);

        if (!groupName) {
            setMessage("Group name is required.");
            setLoading(false);
            return;
        }

        const isGroupNameAvailable = await checkGroupName(groupName);
        if (!isGroupNameAvailable) {
            setMessage("Group name already exists.");
            setLoading(false);
            return;
        }

        try {
            if (firebaseService) {

                const createGroup = httpsCallable(firebaseService.functions, "createGroup");
                const result = await createGroup({
                    groupName: groupName,
                });

                console.log("Firebase response:", result.data);
                // If the response is okay
                toast.success("Group created successfully!");

                onGroupAdded(result.data as Group);
                setOpen(false);
            }
        } catch (error) {
            setMessage("Failed to create group.");
        }
        setLoading(false);
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setMessage("");
    };

    return (
        <div>
            <Button variant="contained" color="primary" onClick={handleClickOpen} style={{ marginBottom: 16 }}>
                Create
            </Button>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Create new Group</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <TextField
                            label="Group Name"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            fullWidth
                            margin="normal"
                        />
                    )}

                    {message && (
                        <Typography variant="body1" color="error" style={{ marginTop: 16 }}>
                            {message}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleCreateGroup} disabled={loading} color="primary">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
